import type { GuildWarCandidatePoolMember } from "@/core/guild-war/types"

import {
  getGuildWarPathSearchTokens,
  toGuildWarPathDisplayLabel,
  toGuildWarPathKey,
  toGuildWarPathPairDisplayLabel,
} from "@/lib/guild-war-path-display"

export type { SecondaryPathFilterValue } from "@/core/guild-war/types"
import type {
  GuildWarAbsenceRequestedFor,
  GuildWarCandidateSource,
  GuildWarWeeklyAvailability,
  MartialArtPath,
  SecondaryPathFilterValue,
} from "@/core/guild-war/types"

type AbsenceState = "none" | "pending" | "approved"
type CandidatePoolSortBy =
  | "member"
  | "availability"
  | "primaryPath"
  | "secondaryPath"
  | "source"
  | "absenceState"
type CandidatePoolSortOrder = "asc" | "desc"

const includesDay = (
  availability: GuildWarWeeklyAvailability,
  day: GuildWarAbsenceRequestedFor,
) => {
  if (day === "both") {
    return availability === "both"
  }

  if (day === "saturday") {
    return availability === "saturday" || availability === "both"
  }

  return availability === "sunday" || availability === "both"
}

const matchesCandidateSourceFilter = (
  source: GuildWarCandidateSource,
  filter: GuildWarCandidateSource,
) => {
  if (filter === "both") {
    return true
  }

  if (filter === "permanent") {
    return source !== "weekly"
  }

  return source !== "permanent"
}

const compareNullableStrings = (
  left: string | null | undefined,
  right: string | null | undefined,
) => {
  if (left && right) {
    return left.localeCompare(right)
  }

  if (left && !right) {
    return -1
  }

  if (!left && right) {
    return 1
  }

  return 0
}

const SOURCE_SORT_RANK: Record<GuildWarCandidateSource, number> = {
  permanent: 0,
  both: 1,
  weekly: 2,
}

const ABSENCE_SORT_RANK: Record<AbsenceState, number> = {
  none: 0,
  pending: 1,
  approved: 2,
}

const compareCandidatesBySort = (
  left: GuildWarCandidatePoolMember,
  right: GuildWarCandidatePoolMember,
  sortBy: CandidatePoolSortBy,
) => {
  if (sortBy === "member") {
    return compareNullableStrings(
      left.guildDisplayName || left.memberId,
      right.guildDisplayName || right.memberId,
    )
  }

  if (sortBy === "primaryPath") {
    return compareNullableStrings(left.primaryPathCode, right.primaryPathCode)
  }

  if (sortBy === "secondaryPath") {
    return compareNullableStrings(
      left.secondaryPathCode,
      right.secondaryPathCode,
    )
  }

  if (sortBy === "source") {
    return SOURCE_SORT_RANK[left.source] - SOURCE_SORT_RANK[right.source]
  }

  if (sortBy === "absenceState") {
    return (
      ABSENCE_SORT_RANK[left.absenceState] -
      ABSENCE_SORT_RANK[right.absenceState]
    )
  }

  return compareNullableStrings(left[sortBy], right[sortBy])
}

export const resolveCandidatePrimaryPathKey = (
  candidate: GuildWarCandidatePoolMember,
) => {
  return toGuildWarPathKey(candidate.primaryPathCode)
}

export const resolveCandidateSecondaryPathKey = (
  candidate: GuildWarCandidatePoolMember,
) => {
  return toGuildWarPathKey(candidate.secondaryPathCode)
}

export const formatCandidatePrimaryPathLabel = (
  candidate: GuildWarCandidatePoolMember,
): string => {
  const value = candidate.primaryPathCode
  return toGuildWarPathDisplayLabel(value, {
    variant: "short",
    fallback: value ?? "-",
  })
}

export const formatCandidateSecondaryPathLabel = (
  candidate: GuildWarCandidatePoolMember,
): string => {
  const value = candidate.secondaryPathCode
  if (!value) {
    return "None"
  }

  return toGuildWarPathDisplayLabel(value, {
    variant: "short",
    fallback: value,
  })
}

export const formatCandidatePathPairLabel = (
  candidate: GuildWarCandidatePoolMember,
): string => {
  return toGuildWarPathPairDisplayLabel(
    candidate.primaryPathCode,
    candidate.secondaryPathCode,
    {
      variant: "short",
      noneLabel: "None",
    },
  )
}

export const matchesSecondaryPathFilter = (
  candidate: GuildWarCandidatePoolMember,
  filter: SecondaryPathFilterValue,
): boolean => {
  if (filter === "all") {
    return true
  }

  const secondary = resolveCandidateSecondaryPathKey(candidate)
  if (filter === "none") {
    return secondary === null
  }

  return secondary === filter
}

export const buildCandidateSearchHaystack = (
  candidate: GuildWarCandidatePoolMember,
): string => {
  return [
    candidate.guildDisplayName,
    candidate.memberId,
    candidate.source,
    candidate.availability,
    candidate.absenceState,
    candidate.primaryPathCode,
    candidate.secondaryPathCode,
    ...getGuildWarPathSearchTokens(candidate.primaryPathCode),
    ...getGuildWarPathSearchTokens(candidate.secondaryPathCode),
  ]
    .filter((token): token is string => typeof token === "string")
    .join(" ")
    .toLowerCase()
}

export const applyCandidatePoolClientFilters = (
  items: GuildWarCandidatePoolMember[],
  input: {
    day?: "all" | GuildWarAbsenceRequestedFor
    source?: GuildWarCandidateSource
    paths?: MartialArtPath[]
    secondaryPath: SecondaryPathFilterValue
    absenceState?: "all" | AbsenceState
    search: string
    sortBy?: CandidatePoolSortBy
    sortOrder?: CandidatePoolSortOrder
  },
) => {
  const normalizedSearch = input.search.trim().toLowerCase()

  const filtered = items.filter((candidate) => {
    if (
      input.day &&
      input.day !== "all" &&
      !includesDay(candidate.availability, input.day)
    ) {
      return false
    }

    if (
      input.source &&
      !matchesCandidateSourceFilter(candidate.source, input.source)
    ) {
      return false
    }

    if (input.paths && input.paths.length > 0) {
      const primaryPath = resolveCandidatePrimaryPathKey(candidate)
      if (!primaryPath || !input.paths.includes(primaryPath)) {
        return false
      }
    }

    if (!matchesSecondaryPathFilter(candidate, input.secondaryPath)) {
      return false
    }

    if (
      input.absenceState &&
      input.absenceState !== "all" &&
      candidate.absenceState !== input.absenceState
    ) {
      return false
    }

    if (!normalizedSearch) {
      return true
    }

    return buildCandidateSearchHaystack(candidate).includes(normalizedSearch)
  })

  if (!input.sortBy) {
    return filtered
  }

  const sorted = [...filtered].sort((left, right) =>
    compareCandidatesBySort(left, right, input.sortBy ?? "member"),
  )

  if ((input.sortOrder ?? "asc") === "desc") {
    sorted.reverse()
  }

  return sorted
}
